import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

/** Follow the OS continuously, including a theme change while the app is open.
 * CSS media queries own web colors; the native status bar mirrors that same choice.
 * Keyboard style intentionally stays unspecified so the OS chooses its appearance.
 */
export function watchSystemAppearance(): () => void {
  if (typeof window.matchMedia !== "function") return () => {};
  const preference = window.matchMedia("(prefers-color-scheme: dark)");
  let stopped = false;
  let revision = 0;
  let pending = Promise.resolve();
  const update = () => {
    const dark = preference.matches;
    const color = dark ? "#111b24" : "#f8f5ef";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", color);
    if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("StatusBar")) return;
    const current = ++revision;
    // Serialize bridge operations and discard superseded changes to prevent an
    // old asynchronous update repainting a newer system appearance.
    pending = pending.then(async () => {
      if (stopped || current !== revision) return;
      try {
        // Capacitor names styles for the background, not the text color.
        await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
        if (Capacitor.getPlatform() === "android" && !stopped && current === revision) {
          // New edge-to-edge Android versions ignore this; CSS paints their inset.
          await StatusBar.setBackgroundColor({ color });
        }
      } catch { /* Old shells/browser fallback keep the OS defaults. */ }
    });
  };
  preference.addEventListener("change", update);
  update();
  return () => { stopped = true; revision++; preference.removeEventListener("change", update); };
}
